import React, { ComponentProps, useCallback, useRef } from "react";

import { TRACK_HEIGHT } from "../../constants";
import { useDragCursor } from "../../hooks/use-drag-cursor";
import {
  calculateNewPosition,
  getFromFromLeft,
  getItemLeftOffset,
  getItemWidth,
} from "../../utils/position-utils";
import { ImageIcon } from "../icons";

import { PropsWithChildren } from "react";
import { useTimelineContext } from "../../context/use-timeline-context";
import { ItemType, SolidItem } from "../../types";
import {
  ExtendButtonWrapper,
  InnerExtendHandle,
  InnerSimplePreview,
  SimplePreviewWrapper,
  TimelineItemWrapper,
} from "./styled";
import { getIcon } from "../../../../../../../utils/get-icon";
import { PlayerRef } from "@remotion/player";

const SimplePreview = ({ children, style }: ComponentProps<"div">) => {
  return (
    <SimplePreviewWrapper style={{ background: "#FF9843", ...style }}>
      {children}
    </SimplePreviewWrapper>
  );
};

const Icon = ({ children }: PropsWithChildren) => {
  return <span>{children}</span>;
};

const Text = ({ children }: PropsWithChildren) => {
  return <span className="truncate">{children}</span>;
};

SimplePreview.Icon = Icon;
SimplePreview.Text = Text;

const EXTEND_HANDLE_TARGET_WIDTH = 12;

const ExtendButtonRefForward: React.ForwardRefRenderFunction<
  HTMLDivElement,
  ComponentProps<"div">
> = ({ className, ...restProps }, ref) => {
  return (
    <ExtendButtonWrapper
      ref={ref}
      className={className}
      {...restProps}
    ></ExtendButtonWrapper>
  );
};

const ExtendHandle = React.forwardRef(ExtendButtonRefForward);

const TextItemPreview: React.FC = () => {
  return (
    <SimplePreview style={{ background: "#344758" }}>
      <SimplePreview.Icon>{getIcon("item-text")}</SimplePreview.Icon>
      <SimplePreview.Text>Text</SimplePreview.Text>
    </SimplePreview>
  );
};

const ImageItemPreview: React.FC = () => {
  return (
    <SimplePreview style={{ background: "#4F6F52" }}>
      <SimplePreview.Icon>
        <ImageIcon />
      </SimplePreview.Icon>
      <SimplePreview.Text>Image</SimplePreview.Text>
    </SimplePreview>
  );
};

const VideoItemPreview: React.FC = () => {
  return (
    <SimplePreview style={{ background: "#2f8de4" }}>
      <SimplePreview.Icon>{getIcon("item-video")}</SimplePreview.Icon>
      <SimplePreview.Text>Video</SimplePreview.Text>
    </SimplePreview>
  );
};

const SolidItemPreview: React.FC<{
  item: SolidItem;
}> = ({ item }) => {
  return (
    <SimplePreview style={{ background: "#9973A0" }}>
      <InnerSimplePreview
        style={{
          backgroundColor: item.color,
        }}
      ></InnerSimplePreview>
      <SimplePreview.Text>Solid</SimplePreview.Text>
    </SimplePreview>
  );
};

const ItemPreview: React.FC<{
  item: ItemType;
}> = ({ item }) => {
  if (item.type === "text") {
    return <TextItemPreview />;
  }

  if (item.type === "image") {
    return <ImageItemPreview />;
  }

  if (item.type === "video") {
    return <VideoItemPreview />;
  }

  if (item.type === "solid") {
    return <SolidItemPreview item={item} />;
  }

  throw new Error(`Unknown item type: ${JSON.stringify(item)}`);
};

const TimelineItem = ({
  item,
  trackIndex,
  timelineWidth,
  visibleFrames,
  playerRef,
}: {
  item: ItemType;
  trackIndex: number;
  timelineWidth: number;
  visibleFrames: number;
  playerRef?: React.RefObject<PlayerRef | null>;
}) => {
  const { changeItem, tracks, setTracks, setSelectedItem, selectedItem, fps } =
    useTimelineContext();
  const timelineItemRef = useRef<HTMLDivElement>(null);
  const extendHandleLeftRef = useRef<HTMLDivElement>(null);
  const extendHandleRightRef = useRef<HTMLDivElement>(null);

  const { startDrag, stopDrag, DragOverlay } = useDragCursor();

  const seekToItemStart = useCallback(() => {
    if (playerRef?.current) {
      playerRef.current.seekTo(item.from);
    }
  }, [playerRef, item.from]);

  const onPointerDownHandleLeft = useCallback(
    (pointerDownEvent: React.PointerEvent<HTMLDivElement>) => {
      if (pointerDownEvent.button !== 0) {
        return;
      }

      pointerDownEvent.preventDefault();
      startDrag("ew-resize");

      const initialFrom = item.from;
      const startX = pointerDownEvent.clientX;

      const trackItemsSorted = tracks[trackIndex].items.sort(
        (a, b) => a.from - b.from
      );

      const itemIndex = trackItemsSorted.findIndex(
        (trackItem) => trackItem.id === item.id
      );

      const previousItem = trackItemsSorted[itemIndex - 1];

      const previousItemEnd = previousItem
        ? previousItem.from + previousItem.durationInFrames
        : 0;

      const onPointerMove = (pointerMoveEvent: PointerEvent) => {
        const offsetX = pointerMoveEvent.clientX - startX;
        const itemLeft = getItemLeftOffset({
          timelineWidth,
          totalDurationInFrames: visibleFrames,
          from: item.from,
        });

        const left = Math.max(0, itemLeft + offsetX);

        const from = getFromFromLeft({
          timelineWidth,
          left,
          totalDurationInFrames: visibleFrames,
        });

        const minFrom =
          item.type === "video"
            ? Math.max(
                previousItemEnd,
                from,
                initialFrom - Math.floor(item.videoStartFromInSeconds * fps)
              )
            : Math.max(previousItemEnd, from);

        const maxFrom = Math.min(
          item.from + item.durationInFrames - 1,
          minFrom
        );

        const newDurationInFrames = item.from + item.durationInFrames - maxFrom;

        const finalDurationInFrames =
          item.type === "video"
            ? Math.min(
                newDurationInFrames,
                Math.floor(item.videoDurationInSeconds * fps)
              )
            : newDurationInFrames;

        changeItem(item.id, (prevItem) => {
          if (prevItem.type !== "video" || item.type !== "video") {
            return {
              ...prevItem,
              durationInFrames: finalDurationInFrames,
              from: maxFrom,
              isDragging: true,
            };
          }

          const framesDelta = maxFrom - initialFrom;
          const newStartFromSeconds = Math.max(
            0,
            item.videoStartFromInSeconds + framesDelta / fps
          );

          return {
            ...prevItem,
            durationInFrames: finalDurationInFrames,
            from: maxFrom,
            isDragging: true,
            videoStartFromInSeconds: newStartFromSeconds,
          };
        });
      };

      const onPointerUp = () => {
        stopDrag();
        changeItem(item.id, (previousItem) => {
          return {
            ...previousItem,
            isDragging: false,
          };
        });
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
    },
    [
      item,
      changeItem,
      visibleFrames,
      trackIndex,
      tracks,
      timelineWidth,
      fps,
      startDrag,
      stopDrag,
    ]
  );

  const onPointerDownHandleRight = useCallback(
    (pointerDownEvent: React.PointerEvent<HTMLDivElement>) => {
      if (pointerDownEvent.button !== 0) {
        return;
      }

      pointerDownEvent.preventDefault();
      startDrag("ew-resize");

      const startX = pointerDownEvent.clientX;

      const trackItemsSorted = tracks[trackIndex].items.sort(
        (a, b) => a.from - b.from
      );

      const itemIndex = trackItemsSorted.findIndex(
        (trackItem) => trackItem.id === item.id
      );

      const nextItem = trackItemsSorted[itemIndex + 1];

      const nextItemFrom = nextItem?.from ?? Infinity;

      const onPointerMove = (pointerMoveEvent: PointerEvent) => {
        const offsetX = pointerMoveEvent.clientX - startX;
        const itemWidth = getItemWidth({
          trackDurationInFrames: item.durationInFrames,
          timelineWidth,
          totalDurationInFrames: visibleFrames,
        });

        const maxDuration = nextItemFrom - item.from;

        const minDuration = 1;

        const newWidth = itemWidth + offsetX;
        const ratio = newWidth / itemWidth;
        let newDurationInFrames = Math.max(
          minDuration,
          Math.min(maxDuration, Math.round(item.durationInFrames * ratio))
        );

        if (item.type === "video") {
          const videoStartFrame = Math.floor(
            item.videoStartFromInSeconds * fps
          );
          const availableFrames =
            Math.floor(item.videoDurationInSeconds * fps) - videoStartFrame;
          newDurationInFrames = Math.min(newDurationInFrames, availableFrames);
        }

        changeItem(item.id, (previousItem) => ({
          ...previousItem,
          durationInFrames: newDurationInFrames,
          isDragging: true,
        }));
      };

      const onPointerUp = () => {
        stopDrag();
        changeItem(item.id, (previousItem) => {
          return {
            ...previousItem,
            isDragging: false,
          };
        });
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
    },
    [
      item,
      visibleFrames,
      tracks,
      trackIndex,
      changeItem,
      timelineWidth,
      fps,
      startDrag,
      stopDrag,
    ]
  );

  const updatePosition = useCallback(
    ({
      pointerEvent,
      startX,
      startY,
      isDragging,
    }: {
      startX: number;
      startY: number;
      pointerEvent: PointerEvent;
      isDragging: boolean;
    }) => {
      const offsetX = pointerEvent.clientX - startX;
      const offsetY = pointerEvent.clientY - startY;

      const newPosition = calculateNewPosition({
        initialFrom: item.from,
        durationInFrames: item.durationInFrames,
        timelineWidth,
        offsetX,
        offsetY,
        trackIndex: trackIndex,
        tracks,
        visibleFrames,
        itemId: item.id,
      });
      if (newPosition === null) {
        return null;
      }
      setTracks((prevTracks) => {
        return prevTracks.map((track, _trackIdx) => {
          const newItems = track.items.filter((_item) => item.id !== _item.id);

          if (newPosition.track === _trackIdx) {
            newItems.push({
              ...item,
              from: newPosition.from,
              durationInFrames: newPosition.durationInFrames,
              isDragging,
            });
          }

          return {
            ...track,
            items: newItems,
          };
        });
      });
    },
    [item, setTracks, timelineWidth, visibleFrames, trackIndex, tracks]
  );

  const onPointerDownLabel = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) {
        return;
      }
      if (
        e.target === extendHandleLeftRef.current ||
        e.target === extendHandleRightRef.current ||
        extendHandleLeftRef.current?.contains(e.target as Node) ||
        extendHandleRightRef.current?.contains(e.target as Node)
      ) {
        return;
      }

      const { current } = timelineItemRef;
      if (!current) {
        throw new Error("no ref");
      }

      if (item.type !== "video") {
        setSelectedItem(item.id);
      }

      seekToItemStart();

      startDrag("grabbing");

      const startX = e.clientX;
      const startY = e.clientY;

      const onPointerMove = (pointerMoveEvent: PointerEvent) => {
        updatePosition({
          startX,
          startY,
          pointerEvent: pointerMoveEvent,
          isDragging: true,
        });
      };

      const onPointerUp = (pointerUpEvent: PointerEvent) => {
        stopDrag();
        current.style.transform = "";
        current.style.zIndex = "0";

        updatePosition({
          startX,
          startY,
          pointerEvent: pointerUpEvent,
          isDragging: false,
        });

        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
    },
    [
      item.type,
      item.id,
      seekToItemStart,
      startDrag,
      setSelectedItem,
      updatePosition,
      stopDrag,
    ]
  );

  const timelineItemWidth = getItemWidth({
    trackDurationInFrames: item.durationInFrames,
    timelineWidth,
    totalDurationInFrames: visibleFrames,
  });

  const timelineItemLeft = getItemLeftOffset({
    timelineWidth,
    totalDurationInFrames: visibleFrames,
    from: item.from,
  });

  return (
    <TimelineItemWrapper
      isSelected={selectedItem === item.id}
      ref={timelineItemRef}
      onPointerDown={onPointerDownLabel}
      style={{
        width: timelineItemWidth,
        height: TRACK_HEIGHT - 2,
        left: timelineItemLeft,
        top: trackIndex * TRACK_HEIGHT,
        overflow: "hidden",
      }}
    >
      <ExtendHandle
        ref={extendHandleLeftRef}
        onPointerDown={onPointerDownHandleLeft}
        style={{
          width: EXTEND_HANDLE_TARGET_WIDTH,
          marginLeft: -EXTEND_HANDLE_TARGET_WIDTH / 2,
        }}
      />
      <InnerExtendHandle>
        <ItemPreview item={item} />
      </InnerExtendHandle>
      <ExtendHandle
        ref={extendHandleRightRef}
        onPointerDown={onPointerDownHandleRight}
        className="right-0"
        style={{
          width: EXTEND_HANDLE_TARGET_WIDTH,
          marginRight: -EXTEND_HANDLE_TARGET_WIDTH / 2,
        }}
      />
      {DragOverlay}
    </TimelineItemWrapper>
  );
};

export default TimelineItem;
